import type { PerformanceThresholds } from '~/features/my-wallet/types/charts'
import type { WalletPaymentModel } from '~/store-v2/my-wallet/useWalletPaymentModel'
import { MODEL_KEYS, type ModelKey } from '~/features/my-wallet/constants/my-wallet'
import date from '~/utils/date'

/** The API has two blocked signals and does not guarantee one implies the other; either one counts. */
export function isWalletBlocked(summary: Pick<WalletPaymentModel['summary'], 'state' | 'is_blocked'>) {
  return summary.state === 'BLOCKED' || summary.is_blocked
}

export function toKnownModelKey(modelValue: string | null | undefined): ModelKey | null {
  return (MODEL_KEYS as readonly string[]).includes(modelValue ?? '') ? modelValue as ModelKey : null
}

// Novices must use Model C; with a commercial exception every model is unlocked, so A is the best.
const BEST_AVAILABLE_MODEL_BY_STATE: Partial<Record<string, ModelKey>> = {
  NOVICE: 'C',
  COMMERCIAL_EXCEPTION: 'A',
}

export function resolveBestAvailableModel(summary: Pick<WalletPaymentModel['summary'], 'state' | 'is_blocked' | 'current_model'>): ModelKey {
  if (isWalletBlocked(summary))
    return 'C'
  return BEST_AVAILABLE_MODEL_BY_STATE[summary.state] ?? toKnownModelKey(summary.current_model) ?? 'C'
}

/** Falls back to the cards' `min_on_time_percentage` when the `performance` block is missing. */
export function resolveThresholds(paymentModel: WalletPaymentModel): PerformanceThresholds | null {
  if (paymentModel.performance?.thresholds) {
    return {
      modelB: paymentModel.performance.thresholds.model_b,
      modelA: paymentModel.performance.thresholds.model_a,
    }
  }

  const modelB = paymentModel.models?.find(modelCard => modelCard.key === 'B')?.min_on_time_percentage
  const modelA = paymentModel.models?.find(modelCard => modelCard.key === 'A')?.min_on_time_percentage
  if (modelB == null || modelA == null)
    return null

  return { modelB, modelA }
}

export function formatUpdatedAt(updatedAt: string) {
  const batchDateTime = date(updatedAt).tz()
  return {
    date: batchDateTime.format('DD/MM/YYYY'),
    time: batchDateTime.format('HH:mm'),
  }
}

export function resolveReasonKey(keyPrefix: string, reason: string | null | undefined, hasKey: (key: string) => boolean) {
  const reasonKeyPath = reason ? `${keyPrefix}.${reason}` : ''
  return reasonKeyPath && hasKey(reasonKeyPath) ? reasonKeyPath : `${keyPrefix}.fallback`
}
